import { DatePipe, LowerCasePipe, UpperCasePipe } from '@angular/common';
import { Component, computed, inject, OnInit, signal, viewChildren } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import { AccountService } from '@infodocs/shared/services/account.service';
import { AuthService } from '@infodocs/shared/services/auth.service';
import type { UserProfile } from '@infodocs/shared/interfaces/user';
import { CompanyService } from '@infodocs/company/company.service';
import { AddCompanyModalComponent } from '@infodocs/portfolio/modals/add-company-modal/add-company-modal.component';
import {
	RestoreCompanyModalComponent
} from '@infodocs/portfolio/modals/restore-company-modal/restore-company-modal.component';
import { CompanyArchive } from '@infodocs/portfolio/interfaces/company-archive';
import { CompanyArchiveService } from '@infodocs/portfolio/services/company-archive.service';
import { RestoreCompanyJourneyLoggerService } from '@infodocs/portfolio/services/restore-company-journey-logger.service';
import { CompanyLimitNoticeComponent } from '@infodocs/portfolio/components/company-limit-notice/company-limit-notice.component';
import {
	CompanyAddedJourneyLoggerService,
	type CompanyAddSearchLabel,
} from '@infodocs/portfolio/services/company-added-journey-logger.service';
import { ComponentLoaderComponent } from '@infodocs/shared/components/component-loader/component-loader.component';
import { StepComponent } from '@infodocs/shared/components/step/step.component';
import { StepperComponent } from '@infodocs/shared/components/stepper/stepper.component';
import type { VerifyReviewFormValue } from '@infodocs/shared/forms/verify-review-form/verify-review-form.component';
import { VerifyReviewFormComponent } from '@infodocs/shared/forms/verify-review-form/verify-review-form.component';
import { HasObservablesComponent } from '@infodocs/shared/has-observables.component';
import { CompanyExists, CompanySearchResult } from '@infodocs/shared/interfaces/company-search-result';
import { IconComponent } from '@infodocs/shared/components/icon/icon.component';
import { CountryAliases } from '@infodocs/shared/variables/addresses';
import { EVENT_LOGGER_SERVICE } from '@infodocs/shared/modules/event-logging/token';
import { SharedService } from '@infodocs/shared/shared.service';
import { getCompanyDetailsErrorMessage, getCompanyAddErrorMessage } from '@infodocs/shared/utils/company-search-errors';
import { parseCalendarDate } from '@infodocs/shared/utils/dates';
import { AlertComponent } from 'ngx-bootstrap/alert';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { takeUntil, finalize } from 'rxjs';
import type { ExtractedIdentity } from '@infodocs/user/services/user-verification.service';
import { LEVEL_2_VERIFIED_MESSAGE_HTML, UserVerificationService } from '@infodocs/user/services/user-verification.service';
import { UserService } from '@infodocs/user/services/user.service';
import { UploadIdFormComponent } from '@infodocs/user/pages/profile-page/modals/verify-me-modal/upload-id-form/upload-id-form.component';
import { environment } from '@environments/environment';
import { faFolderOpen, faFolder, faCircleNotch } from '@fortawesome/free-solid-svg-icons';

export enum CompanyPreviewStage {
	COMPANY_SUMMARY,
	REQUEST_ACCESS_CHOICE,
	REQUEST_PREVIEW,
	VERIFY_UPLOAD,
	VERIFY_REVIEW,
	IMPORTING,
	SUCCESS,
	COMPANY_LIMIT,
}

export type CompanyExistsStatus = CompanyExists;

export function mergeCompanyExistsStatus(
	fromSearch: CompanyExistsStatus | undefined,
	fromImport: CompanyExistsStatus
): CompanyExistsStatus {
	if (fromSearch === 'Already added' || fromImport === 'Already added') {
		return 'Already added';
	}

	if (fromSearch === 'Request access' || fromImport === 'Request access') {
		return 'Request access';
	}

	if (fromSearch === 'Archived' || fromImport === 'Archived') {
		return 'Archived';
	}

	return fromImport;
}

/** Matches backend UserCompanyService: level > CONFIRMED (IDENTIFIED=2 or VERIFIED=3). */
export function isIdentifiedOrHigher(verificationLevel: number | null | undefined): boolean {
	return verificationLevel != null && verificationLevel >= 2;
}

@Component({
	selector: 'app-company-preview',
	imports: [
		ComponentLoaderComponent,
		DatePipe,
		FormsModule,
		IconComponent,
		LowerCasePipe,
		UpperCasePipe,
		StepperComponent,
		StepComponent,
		AlertComponent,
		UploadIdFormComponent,
		VerifyReviewFormComponent,
		CompanyLimitNoticeComponent,
	],
	templateUrl: './company-preview.component.html',
	styleUrl: './company-preview.component.scss'
})
export class CompanyPreviewComponent extends HasObservablesComponent implements OnInit {
	// Plain properties: set via ngx-bootstrap modalService.show initialState (Object.assign overwrites signals).
	public registrationNumber?: string;
	public search_option: CompanyAddSearchLabel = 'Company Search';
	private readonly _companyExists = signal<CompanyExists | undefined>(undefined);
	public get companyExists() {
		return this._companyExists();
	}
	public set companyExists(val: CompanyExists | undefined) {
		if (val !== undefined) this._companyExists.set(val);
	}
	public existingCompanyId?: string;
	public companySearchResult?: CompanySearchResult;
	protected readonly helpKnowledgeBaseUrl = environment.helpKnowledgeBaseUrl;
	protected readonly privacyPolicyUrl = environment.websiteUrl + '/privacy';
	public onBack: VoidFunction = () => { };
	public onFlowComplete: VoidFunction = () => { };
	protected readonly isLoadingCompany = signal(true);
	protected readonly modalRef = inject(BsModalRef);
	protected readonly companyData = signal<any>(undefined);
	protected readonly errorMessage = signal<string | undefined>(undefined);
	protected readonly faAngleUpIcon = faAngleUp;
	protected readonly faAngleDownIcon = faAngleDown;
	protected readonly faFolderOpenIcon = faFolderOpen;
	protected readonly faFolderIcon = faFolder;
	protected readonly faCircleNotchIcon = faCircleNotch;
	protected readonly selectedStage = signal(CompanyPreviewStage.COMPANY_SUMMARY);
	protected readonly modalTitle = computed(() => {
		const stage = this.selectedStage();
		const isRequestAccess =
			stage === CompanyPreviewStage.REQUEST_ACCESS_CHOICE ||
			stage === CompanyPreviewStage.REQUEST_PREVIEW;
		return isRequestAccess ? 'Request Access' : 'Company Preview';
	});
	protected readonly toggleStates = signal<{ [key: string]: boolean }>({
		companySummary: false,
		directorSummary: false
	});
	protected readonly steps = viewChildren(StepComponent);
	protected readonly sharedService = inject(SharedService);
	protected readonly accountService = inject(AccountService);
	protected readonly router = inject(Router);
	private readonly eventLoggerService = inject(EVENT_LOGGER_SERVICE);
	private readonly companyAddedJourneyLogger = inject(CompanyAddedJourneyLoggerService);
	private readonly modalService = inject(BsModalService);
	private readonly archiveService = inject(CompanyArchiveService);
	private readonly restoreJourneyLogger = inject(RestoreCompanyJourneyLoggerService);
	private readonly companyId = signal<number | null>(null);

	protected readonly archive = signal<CompanyArchive | null>(null);
	protected readonly isArchived = computed(() => this.companyExists === 'Archived');
	protected readonly archiveBannerMessage = computed(() =>
		this.isArchived() ? this.buildArchiveBannerMessage(this.archive()) : null
	);
	protected readonly canRestore = computed(() => this.accountService.isAccountAdmin());
	protected readonly requestAccessState = signal<'idle' | 'pending' | 'requested'>('idle');

	protected readonly previewAccountName = signal<string | null>(null);
	protected readonly isRequestPreviewLoading = signal(false);
	protected readonly isRequestPreviewSubmitting = signal(false);
	private readonly authService = inject(AuthService);
	protected readonly defaultIntro = computed(() => {
		const user = this.authService.currentUser();
		const data = this.companyData();
		if (!user || !data) return '';
		const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email;
		const companyDesc = `${data.CompanyName} (${data.CompanyRegistrationNumber})`;
		return `${name} (${user.email}) has requested access to ${companyDesc}.`;
	});

	protected readonly isCloseCorporation = computed(
		() => (this.companyData()?.CompanyType ?? '').toLowerCase().includes('corporation')
	);
	protected readonly annualReturnComplianceLabel = computed((): string | null => {
		const compliant = this.companyData()?.annual_return_compliant;
		if (compliant === true) {
			return 'Compliant';
		}
		if (compliant === false) {
			return 'Non-Compliant';
		}
		return null;
	});
	protected readonly CompanyPreviewStage = CompanyPreviewStage;
	protected readonly requestAccessChoiceSelected = signal<'verify' | 'preview'>('verify');
	protected readonly requestAccessChoiceButtonLabel = 'Next';

	// This Is Me verification flow
	protected verificationDocumentId: number | null = null;
	protected formInitialValue: Partial<VerifyReviewFormValue> | undefined;
	protected readonly verificationError = signal<string | undefined>(undefined);
	protected readonly isVerificationSubmitting = signal(false);
	protected readonly thisIsMeSuccessMessage = signal(false);
	private readonly verificationService = inject(UserVerificationService);
	private readonly userService = inject(UserService);
	private readonly userVerificationLevel = signal<number | null>(null);
	protected readonly verificationNotDirectorResult = signal(false);
	protected readonly showNotDirectorAlertOnRequestStep = computed(() => this.verificationNotDirectorResult());
	protected readonly helpRequestAccessUrl = computed(() => {
		const companyName = this.companyData()?.CompanyName ?? '';
		const summary = encodeURIComponent(`Request access to ${companyName}`);
		return `${environment.helpGeneralEnquiriesUrl}?summary=${summary}`;
	});
	protected readonly verificationSuccessMessageHtml = inject(DomSanitizer).bypassSecurityTrustHtml(
		LEVEL_2_VERIFIED_MESSAGE_HTML
	);

	public ngOnInit() {
		this.seedCompanyExistsFromSearch();
		this.loadArchiveIfNeeded();

		if (this.companySearchResult?.CompanyInformation?.uxHasRequestedAccessToCompany === 'requested') {
			this.requestAccessState.set('requested');
		} else if (this.companySearchResult?.CompanyInformation?.uxHasRequestedAccessToCompany === 'pending') {
			this.requestAccessState.set('pending');
		}

		this.loadUserVerificationLevel();

		this.accountService.importCompany(this.registrationNumber ?? '')
			.pipe(takeUntil(this.destroy$), finalize(() => this.isLoadingCompany.set(false)))
			.subscribe({
				next: res => {
					this.companyData.set(res);
					this.applyCompanyExistsFromImport(res.CompanyExists);
					this.loadArchiveIfNeeded();
				},
				error: err => this.errorMessage.set(getCompanyDetailsErrorMessage(err, this.registrationNumber)),
			});
	}

	private loadArchiveIfNeeded(): void {
		const registrationNumber = this.registrationNumber
			?? this.companySearchResult?.CompanyInformation?.CompanyRegistrationNumber;

		if (this.companyExists !== 'Archived' || !registrationNumber || this.archive()) {
			return;
		}

		this.archiveService
			.findArchiveByRegistration(registrationNumber)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: archive => this.archive.set(archive ?? null),
				error: () => this.archive.set(null),
			});
	}

	private buildArchiveBannerMessage(archive: CompanyArchive | null): string {
		const userName = archive?.archived_by?.trim() || null;
		const dateArchived = archive?.archived_at ? parseCalendarDate(archive.archived_at) : null;

		if (userName && dateArchived) {
			return `This company was archived by ${userName} on ${dateArchived}.`;
		}
		if (dateArchived) {
			return `This company was archived on ${dateArchived}.`;
		}
		if (userName) {
			return `This company was archived by ${userName}.`;
		}

		return 'This company was archived.';
	}

	protected async openRestoreModal(): Promise<void> {
		const archive = this.archive();
		if (!archive || !this.canRestore()) {
			return;
		}

		await this.accountService.refreshAccountProfile();

		if (this.accountService.hasReachedCompanyLimit()) {
			this.selectedStage.set(CompanyPreviewStage.COMPANY_LIMIT);
			return;
		}

		this.restoreJourneyLogger.logPreviewOpened(archive.company_id, false);
		this.modalRef?.hide();

		this.modalService.show(RestoreCompanyModalComponent, {
			ignoreBackdropClick: true,
			initialState: <Partial<RestoreCompanyModalComponent>>{
				archiveId: archive.id,
				companyId: archive.company_id,
				companyName: archive.company_name ?? this.companyData()?.CompanyName ?? 'this company',
				hasShareRegister: Boolean(archive.has_share_register),
				hasLiveShareRegister: Boolean(archive.has_live_share_register),
			},
		});
	}

	private loadUserVerificationLevel(): void {
		this.userService
			.getUserProfile()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (profile: UserProfile) => this.userVerificationLevel.set(profile.verification_level ?? null),
				error: () => this.userVerificationLevel.set(null)
			});
	}

	protected onStepChanged(stage: number) {
		const eventData = {
			event: 'Form Submission',
			form: 'addCompanyForm',
			state: this.steps()[stage]?.tag() ?? ''
		};
		this.eventLoggerService.logEvent(eventData.event, eventData);
	}

	protected closeModalAndBack() {
		this.modalRef?.hide();

		if (this.shouldExitToPortfolio()) {
			this.onFlowComplete();
			return;
		}

		this.onBack();
	}

	private shouldExitToPortfolio(): boolean {
		const stage = this.selectedStage();

		return stage === CompanyPreviewStage.SUCCESS || stage === CompanyPreviewStage.COMPANY_LIMIT;
	}

	protected toggleTableBody(key: string): void {
		this.toggleStates.update(state => ({
			...state,
			[key]: !state[key]
		}));
	}

	protected goToDashboard() {
		const id = this.companyId() ?? this.existingCompanyId;
		if (id) {
			this.modalRef?.hide();

			this.router.navigateByUrl(`/company/profile/${id}`, {
				state: { company_name: this.companyData()?.CompanyName }
			});
		}
	}

	protected goToRequestAccessChoice() {
		// Level 2+ on Request Access means backend already failed the director ID match.
		if (isIdentifiedOrHigher(this.userVerificationLevel())) {
			this.verificationNotDirectorResult.set(true);
		}
		if (this.verificationNotDirectorResult()) {
			this.requestAccessChoiceSelected.set('preview');
		}
		this.selectedStage.set(CompanyPreviewStage.REQUEST_ACCESS_CHOICE);
	}

	protected onRequestAccessChoice(choice: 'verify' | 'preview') {
		if (choice === 'verify') {
			this.selectedStage.set(CompanyPreviewStage.VERIFY_UPLOAD);
		} else {
			this.previewAccountName.set(null);
			this.isRequestPreviewLoading.set(true);
			this.selectedStage.set(CompanyPreviewStage.REQUEST_PREVIEW);
			this.fetchRequestAccessPreview();
		}
	}

	private fetchRequestAccessPreview(): void {
		const regNo = this.companySearchResult?.CompanyInformation?.CompanyRegistrationNumber;
		if (!regNo) {
			this.isRequestPreviewLoading.set(false);
			return;
		}
		this.accountService
			.requestAccessPreview(regNo)
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => this.isRequestPreviewLoading.set(false))
			)
			.subscribe({
				next: res => this.previewAccountName.set(res.account_name),
				error: () => this.previewAccountName.set(null)
			});
	}

	protected onRequestAccessChoiceConfirm() {
		this.onRequestAccessChoice(this.requestAccessChoiceSelected());
	}

	protected requestAccessWithIntro(intro: string) {
		if (!this.companySearchResult?.CompanyInformation || this.isRequestPreviewSubmitting()) return;

		this.companySearchResult.CompanyInformation.uxHasRequestedAccessToCompany = 'pending';
		this.requestAccessState.set('pending');
		this.isRequestPreviewSubmitting.set(true);
		this.accountService
			.requestAccessToCompanyWithOptions(
				this.companySearchResult.CompanyInformation.CompanyRegistrationNumber
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.companySearchResult!.CompanyInformation.uxHasRequestedAccessToCompany = 'requested';
					this.requestAccessState.set('requested');
					this.selectedStage.set(CompanyPreviewStage.COMPANY_SUMMARY);
				},
				error: () => this.requestAccessState.set('idle'),
				complete: () => this.isRequestPreviewSubmitting.set(false)
			});
	}

	protected requestAccess() {
		if (!this.companySearchResult?.CompanyInformation) return;

		this.companySearchResult.CompanyInformation.uxHasRequestedAccessToCompany = 'pending';
		this.requestAccessState.set('pending');
		this.accountService
			.requestAccessToCompany(this.companySearchResult.CompanyInformation.CompanyRegistrationNumber)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.companySearchResult!.CompanyInformation.uxHasRequestedAccessToCompany = 'requested';
					this.requestAccessState.set('requested');
				}
			});
	}

	protected async addCompany(isSubscribed: boolean): Promise<void> {
		await this.accountService.refreshAccountProfile();

		if (this.accountService.hasReachedCompanyLimit()) {
			this.selectedStage.set(CompanyPreviewStage.COMPANY_LIMIT);
			return;
		}

		if (!isSubscribed && this.accountService.hasReachedFlexiPaygLimit()) {
			this.selectedStage.set(CompanyPreviewStage.COMPANY_LIMIT);
			return;
		}

		this.runPortfolioAdd(isSubscribed);
	}

	protected searchDirector(director: { id_no: string; first_names: string; surname: string }) {
		this.modalRef?.hide();

		const displayName = [director.first_names, director.surname].filter(Boolean).join(' ').trim();
		this.modalService.show(AddCompanyModalComponent, {
			initialState: {
				searchOption: 'director',
				searchQuery: displayName,
				directorSearchId: director.id_no
			}
		});
	}

	protected readonly openDirectorFolder = signal(false);
	protected readonly hoveredDirectorId = signal<string | null>(null);

	protected toggleDirectorFolder(open: boolean, directorId: string | null): void {
		this.hoveredDirectorId.set(directorId);
		this.openDirectorFolder.set(open);
	}

	protected onThisIsMe(): void {
		this.thisIsMeSuccessMessage.set(false);
		this.verificationError.set(undefined);
		this.selectedStage.set(CompanyPreviewStage.VERIFY_UPLOAD);
	}

	protected onBackFromVerify(): void {
		this.selectedStage.set(
			this.companyExists === 'Request access'
				? CompanyPreviewStage.REQUEST_ACCESS_CHOICE
				: CompanyPreviewStage.COMPANY_SUMMARY
		);
	}

	protected onBackFromRequestAccessChoice(): void {
		this.selectedStage.set(CompanyPreviewStage.COMPANY_SUMMARY);
	}

	protected onUploadSuccess(payload: { document_id: number; extracted: ExtractedIdentity }): void {
		this.verificationDocumentId = payload.document_id;
		const ext = payload.extracted || {};
		const citizen = this.resolveCitizen(ext.citizen, ext.citizenship);
		const { first_name, second_name, surname } = this.parseFullName(ext.full_name);
		this.formInitialValue = {
			first_name,
			second_name,
			surname,
			citizen,
			id_number: ext.id_number ?? '',
			date_of_birth: ext.date_of_birth ?? '',
			issue_date: ext.issue_date ?? '',
			expiry_date: ext.expiry_date ?? '',
			country_of_birth: this.resolveCountryId(ext.country_of_birth),
			country_of_residence: this.resolveCountryId(ext.country_of_residence)
		};
		this.selectedStage.set(CompanyPreviewStage.VERIFY_REVIEW);
	}

	protected onVerificationFormSubmitted(value: VerifyReviewFormValue): void {
		if (!this.verificationDocumentId || this.isVerificationSubmitting()) return;

		this.isVerificationSubmitting.set(true);
		this.verificationError.set(undefined);

		const fullName = [value.first_name, value.second_name, value.surname].filter(Boolean).join(' ').trim();
		const countryOfBirth = value.country_of_birth != null
			? this.sharedService.countryOptions.find((c) => c.id === value.country_of_birth)?.option ?? null
			: null;
		const countryOfResidence = value.country_of_residence != null
			? this.sharedService.countryOptions.find((c) => c.id === value.country_of_residence)?.option ?? null
			: null;
		const citizenship = value.citizen ? 'South Africa' : null;

		this.verificationService.submitVerificationRequest({
			document_id: this.verificationDocumentId,
			full_name: fullName,
			id_number: value.id_number,
			date_of_birth: value.date_of_birth,
			issue_date: value.issue_date || null,
			expiry_date: value.expiry_date || null,
			country_of_birth: countryOfBirth,
			country_of_residence: countryOfResidence,
			citizenship,
			consent_given: true
		})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => this.refetchAndHandleVerificationResult(),
				error: (err: unknown) => {
					const msg = err && typeof err === 'object' && 'error' in err
						? (err as { error?: { message?: string } }).error?.message
						: 'An error occurred. Please try again.';
					this.verificationError.set(msg || 'An error occurred. Please try again.');
					this.isVerificationSubmitting.set(false);
				},
			});
	}

	private refetchAndHandleVerificationResult(): void {
		this.loadUserVerificationLevel();
		this.accountService.importCompany(this.registrationNumber ?? '')
			.pipe(takeUntil(this.destroy$), finalize(() => this.isVerificationSubmitting.set(false)))
			.subscribe({
				next: res => {
					this.companyData.set(res);
					this.applyCompanyExistsFromImport(res.CompanyExists);
					if (this.companyExists === 'False') {
						this.thisIsMeSuccessMessage.set(true);
					} else {
						this.verificationNotDirectorResult.set(true);
					}
					this.selectedStage.set(CompanyPreviewStage.COMPANY_SUMMARY);
				},
				error: err => {
					this.verificationError.set(getCompanyDetailsErrorMessage(err, this.registrationNumber));
				},
			});
	}

	private runPortfolioAdd(isSubscribed: boolean): void {
		const source = this.companyAddedJourneyLogger.sourceFromSearchOption(this.search_option);

		this.selectedStage.set(CompanyPreviewStage.IMPORTING);
		this.errorMessage.set(undefined);
		this.companyAddedJourneyLogger.resetSession();

		this.accountService
			.addCompany({
				...this.companyData(),
				subscribed: isSubscribed,
				search_option: this.search_option,
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: result => {
					if (!result.access_only) {
						this.accountService.incrementAccountCompanyCount(!isSubscribed);
					}
					localStorage.removeItem(CompanyService.COMPANIES_LIST);
					this.companyId.set(result.id);
					this.selectedStage.set(CompanyPreviewStage.SUCCESS);
					this.companyAddedJourneyLogger.logAdded(source, result.id);
				},
				error: error => {
					this.companyAddedJourneyLogger.logFailed(source, error, this.resolveTrackedCompanyId());

					if (error.status === 403) {
						this.selectedStage.set(CompanyPreviewStage.COMPANY_LIMIT);
						return;
					}

					this.selectedStage.set(CompanyPreviewStage.COMPANY_SUMMARY);
					this.errorMessage.set(getCompanyAddErrorMessage(error, this.registrationNumber));
				},
			});
	}

	private seedCompanyExistsFromSearch(): void {
		if (this.companyExists !== undefined) {
			return;
		}

		const fromSearch =
			this.companySearchResult?.CompanyExists ??
			this.companySearchResult?.CompanyInformation?.CompanyExists;

		if (fromSearch !== undefined) {
			this.companyExists = fromSearch;
		}
	}

	private applyCompanyExistsFromImport(fromImport: CompanyExistsStatus | undefined): void {
		if (fromImport === undefined) {
			return;
		}

		this.companyExists = mergeCompanyExistsStatus(this.companyExists, fromImport);
	}

	private resolveTrackedCompanyId(): number | null {
		const id = this.companyId() ?? this.existingCompanyId;
		if (id == null || id === '') {
			return null;
		}

		const numericId = Number(id);
		return Number.isFinite(numericId) ? numericId : null;
	}

	private parseFullName(
		fullName: string | null | undefined
	): { first_name: string; second_name: string; surname: string } {
		if (!fullName || typeof fullName !== 'string') {
			return { first_name: '', second_name: '', surname: '' };
		}
		const parts = fullName.trim().split(/\s+/).filter(Boolean);
		if (parts.length === 0) return { first_name: '', second_name: '', surname: '' };
		if (parts.length === 1) return { first_name: parts[0], second_name: '', surname: '' };
		if (parts.length === 2) return { first_name: parts[0], second_name: '', surname: parts[1] };
		const surname = parts[parts.length - 1];
		const secondName = parts.slice(1, -1).join(' ');
		return { first_name: parts[0], second_name: secondName, surname };
	}

	private resolveCountryId(extracted: string | null | undefined): number | null {
		if (!extracted || typeof extracted !== 'string') return null;
		const normalized = extracted.trim().toLowerCase();
		const exact = this.sharedService.countryOptions.find(
			(c) => c.option.toLowerCase() === normalized
		);
		if (exact) return exact.id;
		return CountryAliases[normalized] ?? null;
	}

	private resolveCitizen(
		extractedCitizen: boolean | null | undefined,
		extractedCitizenship: string | null | undefined
	): boolean {
		if (extractedCitizen === true || extractedCitizen === false) return extractedCitizen;
		if (!extractedCitizenship || typeof extractedCitizenship !== 'string') return true;
		const normalized = extractedCitizenship.trim().toLowerCase();
		const saNames = ['south africa', 'rsa', 'za'];
		return saNames.some((s) => normalized === s || normalized.includes(s));
	}
}
